export const WORD_EXEC = "word.exec";
export const EXEC_TIMEOUT_MS = 30_000;
export const MAX_RESULT_BYTES = 20 * 1024;
export const MAX_LOGS = 50;
export const MAX_LOG_LENGTH = 1000;
const MAX_OPS = 20_000;
const MAX_DEPTH = 16;
const MAX_ERROR_LENGTH = 4000;
const ROOT = 0;
const NAME = /^[A-Za-z][A-Za-z0-9]*$/;
const BLOCKED_NAMES = new Set([
    "constructor",
    "prototype",
    "context",
    "sync",
    "then",
    "toJSON",
    "toString",
    "toLocaleString",
    "valueOf",
    "hasOwnProperty",
    "isPrototypeOf",
    "propertyIsEnumerable"
]);
const BLOCKED_KEYS = new Set([
    "__proto__",
    "constructor",
    "prototype"
]);
const READ_METHODS = new Set([
    "load",
    "track",
    "untrack",
    "select",
    "search"
]);
const TRACKING_MODE = "changeTrackingMode";
const DOCUMENT_EXITS = new Set([
    "save",
    "close"
]);
const REQUIREMENT_SETS = {
    WordApi: 9,
    WordApiDesktop: 4,
    WordApiHiddenDocument: 5,
    WordApiOnline: 1
};
export const FRAME_READY = "word-exec-ready";
export const FRAME_PORT = "word-exec-port";
export const FRAME_STOP = "word-exec-stop";
const FRAME_CSP = "default-src 'none'; script-src 'unsafe-eval' 'unsafe-inline'; worker-src blob:";
const WORKER_LOCKDOWN = `["fetch", "XMLHttpRequest", "WebSocket", "WebSocketStream", "WebTransport", "EventSource", "importScripts",
  "indexedDB", "caches", "BroadcastChannel", "Worker", "SharedWorker", "Request"].forEach(function (name) {
  try { delete self[name]; } catch (ignored) {}
  try { if (name in self) Object.defineProperty(self, name, { value: undefined }); } catch (ignored) {}
});`;
const WORKER_BOOT = `self.onmessage = function (event) {
  self.onmessage = null;
  var port = event.ports[0];
  startWordExecSandbox({
    post: function (message) { port.postMessage(message); },
    listen: function (handler) { port.onmessage = function (message) { handler(message.data); }; },
  });
};`;
const frameScript = (workerSource)=>`var worker = null;
addEventListener("message", function (event) {
  if (event.source !== parent || !event.data) return;
  if (event.data.type === "${FRAME_PORT}" && event.ports[0] && !worker) {
    worker = new Worker(URL.createObjectURL(new Blob([${workerSource}], { type: "text/javascript" })));
    worker.postMessage(null, [event.ports[0]]);
  }
  if (event.data.type === "${FRAME_STOP}" && worker) worker.terminate();
});
parent.postMessage({ type: "${FRAME_READY}" }, "*");`;
export function sandboxSrcdoc(runtime) {
    const workerSource = JSON.stringify(`${WORKER_LOCKDOWN}\n${runtime}\n${WORKER_BOOT}`).replace(/</g, "\\u003c");
    return `<!doctype html><meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}"><script>${frameScript(workerSource)}</script>`;
}
export function supportedRequirements(isSetSupported) {
    const supported = {};
    Object.entries(REQUIREMENT_SETS).forEach(([name, maxMinor])=>{
        for(let minor = 1; minor <= maxMinor; minor++){
            if (isSetSupported(name, `1.${minor}`)) supported[name] = `1.${minor}`;
        }
    });
    return supported;
}
export class SandboxViolation extends Error {
    name = "SandboxViolation";
}
const isRecord = (value)=>typeof value === "object" && value !== null && !Array.isArray(value);
const isPlain = (value)=>isRecord(value) && [
        Object.prototype,
        null
    ].includes(Object.getPrototypeOf(value));
const isId = (value)=>Number.isInteger(value);
const isName = (value)=>typeof value === "string" && NAME.test(value) && !BLOCKED_NAMES.has(value);
export const isReadMethod = (name)=>READ_METHODS.has(name) || /^get[A-Z]/.test(name);
export function execInputProblem(input) {
    if (!isRecord(input)) return "word.exec needs an object input.";
    if (typeof input.code !== "string" || !input.code.trim()) return "word.exec needs code.";
    if (input.mode !== "read" && input.mode !== "write") return 'word.exec mode must be "read" or "write".';
    if (typeof input.summary !== "string") return "word.exec needs a summary.";
    return null;
}
function valueProblem(value, depth, refs) {
    if (depth > MAX_DEPTH) return "a value is nested too deeply";
    if (value === null || value === undefined) return null;
    if ([
        "boolean",
        "number",
        "string"
    ].includes(typeof value)) return null;
    if (Array.isArray(value)) {
        for(let index = 0; index < value.length; index++){
            const problem = valueProblem(value[index], depth + 1, refs);
            if (problem) return problem;
        }
        return null;
    }
    if (!isPlain(value)) return "only plain values and Word objects can be passed";
    const keys = Object.keys(value);
    if (keys.length === 1 && keys[0] === "$handle") {
        if (!isId(value.$handle)) return "a Word object reference is malformed";
        refs.push(value.$handle);
        return null;
    }
    for(let index = 0; index < keys.length; index++){
        if (BLOCKED_KEYS.has(keys[index]) || keys[index] === "$handle") return `the key "${keys[index]}" is not allowed`;
        const problem = valueProblem(value[keys[index]], depth + 1, refs);
        if (problem) return problem;
    }
    return null;
}
function opProblem(op, known, mode) {
    if (!isRecord(op)) return "an operation is malformed";
    if (!isId(op.target) || !known.has(op.target)) return "an operation names an unknown Word object";
    if (!isName(op.name)) return `"${String(op.name)}" is not reachable from the sandbox`;
    const refs = [];
    if (op.kind === "get") {
        if (!isId(op.id) || op.id <= ROOT || known.has(op.id)) return "an operation reuses a Word object id";
        known.add(op.id);
        return null;
    }
    if (op.kind === "set") {
        if (mode === "read") return `read mode cannot set ${op.name}`;
        if (op.name === TRACKING_MODE) return "track changes stay on while the assistant edits";
        const problem = valueProblem(op.value, 0, refs);
        if (problem) return problem;
    } else if (op.kind === "call") {
        if (DOCUMENT_EXITS.has(op.name)) return `the assistant cannot ${op.name} the document`;
        if (mode === "read" && !isReadMethod(op.name)) return `read mode cannot call ${op.name}`;
        if (!Array.isArray(op.args)) return "an operation is malformed";
        const problem = valueProblem(op.args, 0, refs);
        if (problem) return problem;
        if (op.name === "set" && op.args.some((arg)=>isRecord(arg) && TRACKING_MODE in arg)) {
            return "track changes stay on while the assistant edits";
        }
        if (op.id !== undefined) {
            if (!isId(op.id) || op.id <= ROOT || known.has(op.id)) return "an operation reuses a Word object id";
            known.add(op.id);
        }
    } else {
        return "an operation is malformed";
    }
    const unknown = refs.find((ref)=>!known.has(ref));
    return unknown === undefined ? null : "an operation passes an unknown Word object";
}
export function opsProblem(ops, known, mode) {
    if (!Array.isArray(ops) || ops.length > MAX_OPS) return "the batch is malformed";
    const seen = new Set(known);
    for(let index = 0; index < ops.length; index++){
        const problem = opProblem(ops[index], seen, mode);
        if (problem) return problem;
    }
    return null;
}
const toPlain = (value)=>{
    const json = JSON.stringify(value);
    return json === undefined ? undefined : JSON.parse(json);
};
export function errorOf(error) {
    const source = isRecord(error) || error instanceof Error ? error : {};
    const cut = (text)=>String(text ?? "").slice(0, MAX_ERROR_LENGTH);
    const result = {
        name: cut(source.name || "Error"),
        message: cut(source.message ?? (typeof error === "string" ? error : String(error)))
    };
    if (typeof source.code === "string") result.code = cut(source.code);
    if (source.debugInfo !== undefined) {
        try {
            const debugInfo = toPlain(source.debugInfo);
            if (JSON.stringify(debugInfo).length <= MAX_ERROR_LENGTH) result.debugInfo = debugInfo;
        } catch  {
            result.debugInfo = undefined;
        }
    }
    return result;
}
const byteLength = (text)=>new TextEncoder().encode(text).length;
export function truncateResult(json) {
    const size = byteLength(json);
    if (size <= MAX_RESULT_BYTES) return JSON.parse(json);
    const head = new TextDecoder().decode(new TextEncoder().encode(json).slice(0, MAX_RESULT_BYTES));
    return `${head}... [truncated: the result was ${Math.ceil(size / 1024)} KB, only the first 20 KB is shown; return less]`;
}
export function capLogs(logs) {
    const lines = logs.slice(0, MAX_LOGS).map((line)=>line.slice(0, MAX_LOG_LENGTH));
    if (logs.length > MAX_LOGS) lines.push("... more lines not shown");
    return lines;
}
export function wordEnums(namespace) {
    const enums = {};
    Object.keys(namespace).forEach((key)=>{
        const value = namespace[key];
        if (!isRecord(value) || typeof value === "function") return;
        const entries = Object.keys(value);
        if (entries.length && entries.every((entry)=>typeof value[entry] === "string")) {
            enums[key] = {
                ...value
            };
        }
    });
    return enums;
}
function descriptorOf(value, name) {
    for(let level = value; level; level = Object.getPrototypeOf(level)){
        const property = Object.getOwnPropertyDescriptor(level, name);
        if (property) return property;
    }
    return undefined;
}
export function createReplayer(context, model, mode) {
    const handles = new Map([
        [
            ROOT,
            context
        ]
    ]);
    let nextItem = -1;
    const isWordObject = (value)=>value === context || value instanceof model.ClientObject || model.TrackedObjects !== undefined && value instanceof model.TrackedObjects;
    const objectOf = (id)=>{
        const value = handles.get(id);
        if (!isWordObject(value)) throw new TypeError("That Word method returned nothing to use.");
        return value;
    };
    const decode = (value)=>{
        if (Array.isArray(value)) return value.map(decode);
        if (!isPlain(value)) return value;
        if ("$handle" in value) return objectOf(value.$handle);
        const out = {};
        Object.keys(value).forEach((key)=>out[key] = decode(value[key]));
        return out;
    };
    const register = (id, value, name)=>{
        if (id === undefined) return;
        if (value === undefined || value === null || isWordObject(value) || value instanceof model.ClientResult) {
            handles.set(id, value ?? null);
            return;
        }
        throw new SandboxViolation(`${name} returned something outside the Word object model`);
    };
    const replayOne = (op, loaded, results)=>{
        const target = objectOf(op.target);
        if (op.kind === "get") {
            const value = target[op.name];
            if (value === undefined) throw new TypeError(`${op.name} is not a property of this Word object`);
            if (!isWordObject(value)) {
                throw new TypeError(`${op.name} is a value, not a Word object: load it and read it after context.sync()`);
            }
            handles.set(op.id, value);
            return;
        }
        if (op.kind === "set") {
            const property = descriptorOf(target, op.name);
            if (!property || typeof property.value === "function") {
                throw new TypeError(`${op.name} is not a property of this Word object`);
            }
            target[op.name] = decode(op.value);
            return;
        }
        const method = target[op.name];
        if (typeof method !== "function") throw new TypeError(`${op.name} is not a function`);
        const args = op.args.map(decode);
        const value = method.apply(target, args);
        if (op.name === "load") {
            const subject = op.target === ROOT ? op.args[0]?.$handle : op.target;
            if (subject !== undefined) loaded.add(subject);
        }
        register(op.id, value, op.name);
        if (op.id !== undefined && value instanceof model.ClientResult) results.add(op.id);
    };
    const dataOf = (id, data)=>{
        const value = handles.get(id);
        if (!isWordObject(value) || typeof value.toJSON !== "function") return;
        const json = toPlain(value.toJSON()) ?? {};
        if ("isNullObject" in value) json.isNullObject = value.isNullObject;
        let items;
        try {
            items = value.items;
        } catch  {
            items = undefined;
        }
        if (!Array.isArray(items)) {
            data.push({
                id,
                json
            });
            return;
        }
        const ids = items.map((item)=>{
            const itemId = nextItem--;
            handles.set(itemId, item);
            const itemJson = toPlain(item.toJSON()) ?? {};
            if ("isNullObject" in item) itemJson.isNullObject = item.isNullObject;
            data.push({
                id: itemId,
                json: itemJson
            });
            return itemId;
        });
        const { items: _, ...rest } = json;
        data.push({
            id,
            json: rest,
            items: ids
        });
    };
    return {
        known: ()=>new Set(handles.keys()),
        paragraphAt (id) {
            const value = isId(id) ? handles.get(id) : undefined;
            if (!(value instanceof model.Paragraph)) throw new TypeError("redline takes a paragraph");
            return value;
        },
        problem: (ops)=>opsProblem(ops, new Set(handles.keys()), mode),
        async replay (ops, sync) {
            const loaded = new Set();
            const results = new Set();
            ops.forEach((op)=>replayOne(op, loaded, results));
            await sync();
            const data = [];
            loaded.forEach((id)=>dataOf(id, data));
            results.forEach((id)=>{
                const result = handles.get(id);
                data.push({
                    id,
                    kind: "result",
                    json: {
                        value: toPlain(result.value)
                    }
                });
            });
            return data;
        }
    };
}
const failure = (error, logs = [])=>({
        ok: false,
        error,
        logs
    });
function doneOutput(message) {
    if (!Array.isArray(message.logs) || !message.logs.every((line)=>typeof line === "string")) {
        throw new SandboxViolation("the sandbox sent malformed logs");
    }
    const logs = capLogs(message.logs);
    if (message.ok === true && typeof message.result === "string") {
        return {
            ok: true,
            result: truncateResult(message.result),
            logs
        };
    }
    if (message.ok === false && isRecord(message.error)) return failure(errorOf(message.error), logs);
    throw new SandboxViolation("the sandbox sent a malformed result");
}
export function runExecSession(port, options) {
    const { code, mode, context, model, enums, requirements, redline, timeoutMs = EXEC_TIMEOUT_MS, signal } = options;
    const replayer = createReplayer(context, model, mode);
    let isBusy = false;
    let isOver = false;
    let seq = 0;
    return new Promise((resolve)=>{
        const cleanups = [];
        const finish = (output)=>{
            if (isOver) return;
            isOver = true;
            cleanups.forEach((cleanup)=>cleanup());
            port.onmessage = null;
            port.close();
            resolve(output);
        };
        const violate = (message)=>finish(failure({
                name: "SandboxViolation",
                message
            }));
        const onAbort = ()=>violate("the sandbox tried to leave its page");
        const timer = setTimeout(()=>finish(failure({
                name: "Timeout",
                message: `the code did not finish within ${timeoutMs / 1000} s`
            })), timeoutMs);
        signal?.addEventListener("abort", onAbort);
        cleanups.push(()=>clearTimeout(timer), ()=>signal?.removeEventListener("abort", onAbort));
        const answer = async (message, step)=>{
            if (isBusy || message.seq !== seq + 1) return violate("the sandbox synced out of turn");
            isBusy = true;
            seq += 1;
            try {
                const data = await step();
                if (!isOver) port.postMessage({
                    type: "synced",
                    seq,
                    ok: true,
                    data
                });
            } catch (error) {
                if (!isOver) port.postMessage({
                    type: "synced",
                    seq,
                    ok: false,
                    error: errorOf(error)
                });
            } finally{
                isBusy = false;
            }
            return undefined;
        };
        const onSync = (message)=>answer(message, async ()=>{
                const problem = replayer.problem(message.ops);
                if (problem) throw new SandboxViolation(problem);
                return replayer.replay(message.ops, ()=>context.sync());
            });
        const onRedline = (message)=>answer(message, async ()=>{
                if (mode === "read") throw new SandboxViolation("read mode cannot redline");
                const paragraph = replayer.paragraphAt(message.target);
                if (typeof message.text !== "string" || /[\r\n]/.test(message.text)) {
                    throw new TypeError("redline takes one paragraph's text; add new paragraphs with insertParagraph");
                }
                await redline(paragraph, message.text);
                return [];
            });
        port.onmessage = ({ data })=>{
            if (isOver) return;
            if (!isRecord(data)) return violate("the sandbox sent a malformed message");
            if (data.type === "sync") return void onSync(data);
            if (data.type === "redline") return void onRedline(data);
            if (data.type !== "done" || isBusy) return violate("the sandbox sent a malformed message");
            try {
                return finish(doneOutput(data));
            } catch (error) {
                return violate(error instanceof Error ? error.message : String(error));
            }
        };
        if (signal?.aborted) return onAbort();
        port.postMessage({
            type: "run",
            code,
            mode,
            enums,
            requirements
        });
        return undefined;
    });
}
